/**
 * Header - Persistent navigation header with auth controls
 * 
 * Always shows:
 * - App branding
 * - User dropdown (email, billing, settings, sign out)
 */

import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Grid, Lock, ArrowLeft } from 'lucide-react';

export function Header({ onSignIn, onSignUp, onSignOut, onBack, backLabel = 'Back', mobileBackLabel, compactAuth = false }) {
  const { user, isAuthenticated, isAdmin, isMember, isVisitor, access, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [waffleOpen, setWaffleOpen] = useState(false);
  const menuRef = useRef(null);
  const waffleRef = useRef(null);

  const handleSignIn = () => {
    if (onSignIn) {
      onSignIn();
      return;
    }
    const currentPath = location.pathname + location.search;
    const redirectParam = currentPath && currentPath !== '/' ? `?redirect=${encodeURIComponent(currentPath)}` : '';
    navigate(`/signin${redirectParam}`);
  };

  const handleSignUp = () => {
    if (onSignUp) {
      onSignUp();
      return;
    }
    const currentPath = location.pathname + location.search;
    const redirectParam = currentPath && currentPath !== '/' ? `?redirect=${encodeURIComponent(currentPath)}` : '';
    navigate(`/signup${redirectParam}`);
  };

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
    if (onSignOut) onSignOut();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (waffleRef.current && !waffleRef.current.contains(e.target)) {
        setWaffleOpen(false);
      }
    };
    if (menuOpen || waffleOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, waffleOpen]);

  // Determine user badge
  let badge = null;
  let badgeLabel = '';
  if (isAdmin) {
    badge = <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Admin</span>;
    badgeLabel = 'Admin';
  } else if (isMember) {
    badge = <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Member</span>;
    badgeLabel = 'Member';
  } else if (isAuthenticated && isVisitor) {
    badge = <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Visitor</span>;
    badgeLabel = 'Visitor';
  }

  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-20">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo and App Switcher */}
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors py-1.5 px-3 rounded-xl hover:bg-gray-100 cursor-pointer border border-gray-200 shadow-2xs mr-1"
              title={backLabel}
              aria-label={backLabel}
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">{backLabel}</span>
              <span className="sm:hidden">{mobileBackLabel || backLabel}</span>
            </button>
          )}
          {isAuthenticated && isAdmin && (
            <div className="relative" ref={waffleRef}>
              <button 
                onClick={() => setWaffleOpen(!waffleOpen)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600 hover:text-slate-900"
                title="App Switcher"
              >
                <Grid size={20} />
              </button>
              
              {waffleOpen && (
                <div className="absolute left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 py-3 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-4 pb-2 mb-2 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Your Apps</p>
                  </div>
                  
                  <div className="px-2 space-y-1">
                    {/* Translate App Link */}
                    <a 
                      href={access?.translate ? "/translate/home" : "/translate/checkout"}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      onClick={() => setWaffleOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <span className="text-xl">🗣️</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">Exbabel Translate</p>
                          {!access?.translate && <Lock size={12} className="text-slate-400" />}
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1">In-person & Zoom translation</p>
                      </div>
                    </a>

                    {/* Live App Link */}
                    <a 
                      href={access?.live ? "/dashboard/livestream" : "/live/checkout"}
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                      onClick={() => setWaffleOpen(false)}
                    >
                      <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
                        <span className="text-xl">📡</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-900 group-hover:text-purple-600 transition-colors">Exbabel Live</p>
                          {!access?.live && <Lock size={12} className="text-slate-400" />}
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1">Live broadcast & OBS integration</p>
                      </div>
                    </a>
                  </div>
                  
                  <div className="px-2 mt-2 pt-2 border-t border-slate-100">
                    <a 
                      href="/workspace"
                      className="flex items-center justify-center gap-2 p-2 text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                      onClick={() => setWaffleOpen(false)}
                    >
                      <Grid size={16} />
                      View All Apps
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          <a href={isAuthenticated && isAdmin ? "/workspace" : "/"} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            
            <span className="font-black text-2xl tracking-tight text-[#3b5aff]">Exbabel</span>
          </a>
        </div>

        {/* Auth Controls */}
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <div className="relative" ref={menuRef}>
              {/* Clickable user area — triggers dropdown */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer px-2 py-1 rounded-md hover:bg-gray-100 transition-colors"
              >
                <span className="hidden sm:inline">{user?.email}</span>
                {badge}
                {/* Hamburger / triple-line icon */}
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Dropdown menu */}
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
                  {/* User info header */}
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                    <p className="text-xs text-gray-500">{badgeLabel}</p>
                  </div>

                  {/* Billing & Settings — admin only */}
                  {isAdmin && !location.pathname.includes('/dashboard/livestream') && new URLSearchParams(location.search).get('mode') !== 'events' && (
                    <>
                      <a
                        href="/billing"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setMenuOpen(false)}
                      >
                        💳 Billing &amp; Usage
                      </a>
                      <a
                        href="/settings"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setMenuOpen(false)}
                      >
                        ⚙️ Settings
                      </a>
                    </>
                  )}

                  {isAdmin && location.pathname.includes('/dashboard/livestream') && (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        document.getElementById('live-settings')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      ⚙️ Settings &amp; Billing
                    </button>
                  )}

                  {/* Sign Out */}
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    🚪 Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignIn}
                className="rounded-xl border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold shadow-xs text-xs sm:text-sm h-9 px-4 transition-colors cursor-pointer"
              >
                Sign In
              </Button>
              <Button
                size="sm"
                onClick={handleSignUp}
                className={`rounded-xl bg-[#4F46E5] text-white hover:bg-indigo-700 font-semibold shadow-md shadow-indigo-200/50 text-xs sm:text-sm h-9 px-4 transition-colors cursor-pointer ${compactAuth ? 'hidden sm:inline-flex' : ''}`}
              >
                Sign Up
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
